import { useEffect, useRef, useState } from 'react';
import {
  type Candidatura,
  type Impiego,
  type Mancanza,
  type Referenza,
  type Scuola,
  ErroreApi,
  apriCandidatura,
  caricaCurriculum,
  dichiaraConsenso,
  leggiCandidatura,
  mandaCandidatura,
  salvaCandidatura,
} from '../api/candidatura.js';
import { Blocco, Campo, SiNo } from '../componenti/Campi.js';

/**
 * Il modulo di candidatura.
 *
 * Nel v2 è **una pagina sola** con nove blocchi, un centinaio di campi e
 * nessun salvataggio: chi la compila dal telefono, se chiude, ricomincia da
 * capo. Qui sono quattro sezioni, e a ogni passaggio quello che c'è scritto
 * va al sicuro — senza chiedere un account, che su un modulo di candidatura è
 * il modo migliore per perdere candidati.
 *
 * Il testo è in inglese perché lo compilano negli Stati Uniti; i commenti e i
 * nomi restano in italiano, come nel resto del sistema.
 */

const SEZIONI = [
  { chiave: 'persona', numero: 'Step 1', titolo: 'About you' },
  { chiave: 'lavoro', numero: 'Step 2', titolo: 'The job' },
  { chiave: 'esperienza', numero: 'Step 3', titolo: 'Experience' },
  { chiave: 'chiusura', numero: 'Step 4', titolo: 'Finish' },
] as const;

const SCUOLE: { kind: Scuola['kind']; etichetta: string }[] = [
  { kind: 'highSchool', etichetta: 'High school' },
  { kind: 'college', etichetta: 'College' },
  { kind: 'tradeSchool', etichetta: 'Business or trade school' },
  { kind: 'professionalSchool', etichetta: 'Professional school' },
];

const vuoto: Partial<Candidatura> = {
  firstName: '',
  lastName: '',
  email: '',
  schools: [],
  employments: [],
  references: [],
  files: [],
};

/** Quello che si manda al servizio: tutte le risposte, di tutti i passi. */
function corpoDa(d: Partial<Candidatura>): Partial<Candidatura> {
  return {
    firstName: d.firstName,
    lastName: d.lastName,
    middleInitial: d.middleInitial,
    email: d.email,
    phoneAreaCode: d.phoneAreaCode,
    phone: d.phone,
    addressStreet: d.addressStreet,
    city: d.city,
    state: d.state,
    zip: d.zip,
    isOver18: d.isOver18,
    isAuthorizedToWork: d.isAuthorizedToWork,
    hasDriverLicense: d.hasDriverLicense,
    positionDesired: d.positionDesired,
    availableFrom: d.availableFrom || null,
    salaryDesired: d.salaryDesired,
    employedNow: d.employedNow,
    mayContactEmployer: d.mayContactEmployer,
    appliedBefore: d.appliedBefore,
    appliedBeforeWhere: d.appliedBeforeWhere,
    appliedBeforeWhen: d.appliedBeforeWhen,
    referredBy: d.referredBy,
    specialSkills: d.specialSkills,
    currentCertifications: d.currentCertifications,
    militaryService: d.militaryService,
    militaryRank: d.militaryRank,
    nationalGuardReserves: d.nationalGuardReserves,
    convictedOfCrime: d.convictedOfCrime,
    crimeExplanation: d.crimeExplanation,
    emergencyName: d.emergencyName,
    emergencyAddress: d.emergencyAddress,
    emergencyPhone: d.emergencyPhone,
    schools: d.schools,
    employments: d.employments,
    references: d.references,
  };
}

/**
 * Il corpo senza le risposte che il servizio ha rifiutato, perché il resto si
 * salvi lo stesso. Prima una risposta sbagliata — «1990» negli anni di scuola —
 * faceva perdere tutto il passo, anche il nome della scuola. Una risposta di
 * primo livello resta com'era salvata; in una riga (una scuola, un impiego)
 * si svuota solo quella.
 */
function senzaLeSbagliate(corpo: Partial<Candidatura>, sbagliate: Mancanza[]): Partial<Candidatura> {
  const copia = structuredClone(corpo) as Record<string, unknown>;
  for (const { campo } of sbagliate) {
    const [radice, indice, chiave] = campo.split('.');
    if (!radice) continue;
    if (indice === undefined || chiave === undefined) {
      delete copia[radice];
      continue;
    }
    const riga = (copia[radice] as Record<string, unknown>[] | undefined)?.[Number(indice)];
    if (riga) riga[chiave] = null;
  }
  return copia as Partial<Candidatura>;
}

export function Modulo() {
  const [passo, setPasso] = useState(0);
  const [dati, setDati] = useState<Partial<Candidatura>>(vuoto);
  const [caricata, setCaricata] = useState(false);
  const [salvataggio, setSalvataggio] = useState<'fermo' | 'salvo' | 'salvato'>('fermo');
  const [mancanze, setMancanze] = useState<Mancanza[]>([]);
  /** Le risposte che il salvataggio ha rifiutato: «1990» negli anni di scuola. */
  const [sbagliati, setSbagliati] = useState<Mancanza[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [mandata, setMandata] = useState(false);
  /** Il campo trappola: resta vuoto, a meno che non compili un robot. */
  const [trappola, setTrappola] = useState('');
  /**
   * Niente modulo, solo un avviso e Reload: la bozza c'è ma non si è potuta
   * leggere — il modulo la coprirebbe — oppure non c'è più.
   */
  const [guasto, setGuasto] = useState(false);
  /** I salvataggi in fila: uno vecchio non arriva mai dopo uno nuovo. */
  const coda = useRef<Promise<unknown>>(Promise.resolve());
  /** L'ultima versione salvata, per non risalvare quella che c'è già. */
  const ultimoSalvato = useRef('');
  const salvaInFila = (corpo: Partial<Candidatura>) => {
    const giro = coda.current.catch(() => undefined).then(() => salvaCandidatura(corpo));
    coda.current = giro;
    return giro;
  };
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * La bozza non c'è più: scaduta dopo trenta giorni, o tolta, con la pagina
   * ancora aperta. Ogni salvataggio diceva «Something went wrong.», e il
   * candidato continuava a scrivere risposte che non andavano da nessuna
   * parte. Si dice com'è, e ricaricando si riparte da una bozza nuova:
   * all'apertura, se quella di prima non c'è, se ne apre un'altra.
   *
   * Dice se l'errore era questo, e allora l'ha già gestito.
   */
  const bozzaSparita = (e: unknown): boolean => {
    if (!(e instanceof ErroreApi && e.codice === 'nessunaBozza')) return false;
    setGuasto(true);
    setErrore(
      'Your saved answers are no longer here: this application has expired or was removed. ' +
        'Please reload the page to start again.',
    );
    return true;
  };

  /** All'apertura: si riprende quella di prima, o se ne apre una nuova. */
  useEffect(() => {
    void (async () => {
      try {
        const mia = await leggiCandidatura();
        ultimoSalvato.current = JSON.stringify(corpoDa(mia));
        setDati(mia);
      } catch (lettura) {
        // una bozza nuova solo se quella di prima non c'è: con un errore
        // qualsiasi si cambiava il cookie, e la bozza coi dati non si
        // ritrovava più
        if (lettura instanceof ErroreApi && lettura.stato === 404) {
          try {
            await apriCandidatura();
            setDati(vuoto);
          } catch (e) {
            setErrore(e instanceof ErroreApi ? e.message : 'Something went wrong.');
          }
        } else {
          setGuasto(true);
          setErrore(
            lettura instanceof ErroreApi && lettura.stato === 0
              ? lettura.message
              : 'We could not load your saved answers. Please reload the page in a minute: nothing is lost.',
          );
        }
      } finally {
        setCaricata(true);
      }
    })();
  }, []);

  /**
   * «What you type is saved as you go»: due secondi dopo l'ultima risposta si
   * salva da solo. Prima si salvava solo con Continue, Back e Send, e chi
   * chiudeva la pagina perdeva il passo. In silenzio: le risposte sbagliate
   * si segnano in rosso quando si va avanti, non mentre si scrive.
   */
  useEffect(() => {
    if (!caricata || mandata || guasto) return;
    const corpo = corpoDa(dati);
    const firma = JSON.stringify(corpo);
    if (firma === ultimoSalvato.current) return;
    const timer = setTimeout(() => {
      salvaInFila(corpo)
        .then(() => {
          ultimoSalvato.current = firma;
          setSalvataggio((s) => (s === 'salvo' ? s : 'salvato'));
        })
        .catch(async (e) => {
          if (bozzaSparita(e)) return;
          if (e instanceof ErroreApi && e.codice === 'datiNonValidi') {
            const sbagliate = (e.dettagli.fields ?? []) as Mancanza[];
            await salvaInFila(senzaLeSbagliate(corpo, sbagliate)).catch(bozzaSparita);
          }
        });
    }, 2000);
    return () => clearTimeout(timer);
    // salvaInFila cambia a ogni disegno, ma usa solo la coda, che è un ref
  }, [dati, caricata, mandata, guasto]);

  /**
   * Chi ricarica o chiude la pagina entro quei due secondi perdeva l'ultima
   * risposta (collaudo, secondo giro: il nome scritto e subito F5). Lasciando
   * la pagina — o passando a un'altra scheda, che sul telefono è spesso
   * l'ultima cosa che succede — si manda subito quello che manca, con
   * `keepalive`: il browser finisce la richiesta anche a pagina chiusa. Non si
   * segna come salvato: se la pagina resta, il salvataggio normale rifà il
   * suo giro e si occupa delle risposte rifiutate.
   */
  const ultimiDati = useRef(dati);
  ultimiDati.current = dati;
  useEffect(() => {
    if (!caricata || mandata || guasto) return;
    const lascia = () => {
      const firma = JSON.stringify(corpoDa(ultimiDati.current));
      if (firma === ultimoSalvato.current) return;
      void fetch('/api/applications/mine', {
        method: 'PATCH',
        keepalive: true,
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: firma,
      }).catch(() => undefined);
    };
    const nascosta = () => {
      if (document.visibilityState === 'hidden') lascia();
    };
    window.addEventListener('pagehide', lascia);
    document.addEventListener('visibilitychange', nascosta);
    return () => {
      window.removeEventListener('pagehide', lascia);
      document.removeEventListener('visibilitychange', nascosta);
    };
  }, [caricata, mandata, guasto]);

  const cambia = <K extends keyof Candidatura>(chiave: K, valore: Candidatura[K]) =>
    setDati((d) => ({ ...d, [chiave]: valore }));

  const errorePer = (campo: string) =>
    (mancanze.find((m) => m.campo === campo) ?? sbagliati.find((m) => m.campo === campo))?.testo;

  /** Porta il candidato sul campo segnato in rosso, appena è a video. */
  const vaiAlCampoSbagliato = () =>
    requestAnimationFrame(() => {
      const campo = document.querySelector('.campo-sbagliato');
      if (!campo) return window.scrollTo({ top: 0 });
      campo.scrollIntoView({ block: 'center', behavior: 'smooth' });
      campo.querySelector<HTMLElement>('input, select, textarea, button')?.focus({ preventScroll: true });
    });

  /**
   * Salva e va dove si è chiesto. Dice se il salvataggio è riuscito.
   *
   * Avanti si va solo se è riuscito. **Indietro si va sempre**: chi torna a
   * una sezione di prima non deve restare chiuso in questa per un campo
   * sbagliato. Quello che ha scritto resta nella pagina, e il campo resta
   * segnato finché non lo corregge.
   */
  const salvaEVai = async (dove: number): Promise<boolean> => {
    const indietro = dove < passo;
    const corpo = corpoDa(dati);
    setSalvataggio('salvo');
    try {
      await salvaInFila(corpo);
      ultimoSalvato.current = JSON.stringify(corpo);
      setSalvataggio('salvato');
      setErrore(null);
      setSbagliati([]);
      setPasso(dove);
      window.scrollTo({ top: 0 });
      return true;
    } catch (e) {
      setSalvataggio('fermo');
      if (bozzaSparita(e)) return false;
      if (e instanceof ErroreApi && e.codice === 'datiNonValidi') {
        const campi = (e.dettagli.fields ?? []) as Mancanza[];
        setSbagliati(campi);
        // il resto si salva lo stesso: solo le risposte segnate restano da salvare.
        // Il servizio guarda le risposte prima della bozza: che non ci sia più
        // lo si scopre qui
        try {
          await salvaInFila(senzaLeSbagliate(corpo, campi));
        } catch (ancora) {
          if (bozzaSparita(ancora)) return false;
        }
        const primo = campi[0];
        const sezione = sezioneDelCampo(primo?.campo ?? '');
        if (indietro) {
          setErrore(
            `${primo?.testo ?? e.message} This answer is not saved yet` +
              (sezione !== null ? `: you can correct it in step ${sezione + 1}.` : '.'),
          );
          setPasso(dove);
          window.scrollTo({ top: 0 });
        } else {
          setErrore(`Please correct the answer marked in red. ${primo?.testo ?? e.message}`);
          if (sezione !== null) setPasso(sezione);
          vaiAlCampoSbagliato();
        }
        return false;
      }
      setErrore(
        e instanceof ErroreApi
          ? e.message
          : 'We could not save your answers. Check your connection and try again.',
      );
      if (indietro) setPasso(dove);
      return false;
    }
  };

  const manda = async () => {
    setMancanze([]);
    setSalvataggio('salvo');
    try {
      // se non si salva non si manda: partirebbe la versione salvata prima
      if (!(await salvaEVai(passo))) return;
      await mandaCandidatura(trappola);
      setMandata(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setSalvataggio('fermo');
      if (bozzaSparita(e)) return;
      if (e instanceof ErroreApi && e.codice === 'candidaturaIncompleta') {
        const mancano = (e.dettagli.missing ?? []) as Mancanza[];
        setMancanze(mancano);
        // il primo campo che manca dice anche in quale sezione tornare
        const dove = sezioneDelCampo(mancano[0]?.campo ?? '');
        if (dove !== null) setPasso(dove);
        window.scrollTo({ top: 0 });
      } else {
        setErrore(e instanceof ErroreApi ? e.message : 'Something went wrong.');
      }
    }
  };

  const caricaFile = async (file: File) => {
    try {
      const messo = await caricaCurriculum(file);
      setDati((d) => ({ ...d, files: [messo] }));
      setErrore(null);
    } catch (e) {
      if (bozzaSparita(e)) return;
      setErrore(e instanceof ErroreApi ? e.message : 'The upload did not go through.');
    }
  };

  if (!caricata) {
    return (
      <div className="pagina">
        <p className="sottotitolo" style={{ padding: '48px 0' }}>
          One moment…
        </p>
      </div>
    );
  }

  if (mandata) return <Fatta nome={dati.firstName ?? ''} />;

  if (guasto) {
    return (
      <div className="pagina">
        <p className="sottotitolo" role="alert" style={{ padding: '48px 0' }}>
          {errore}
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }

  return (
    <div className="pagina">
      <header className="intestazione">
        <div className="marchio">La Regina Atlantica</div>
        <h1>Application for employment</h1>
        <p className="sottotitolo">
          Four short steps. What you type is saved as you go, so you can close this page and come
          back later on the same device.
        </p>
      </header>

      <ol className="passi">
        {SEZIONI.map((s, i) => (
          <li
            key={s.chiave}
            className={i === passo ? 'passo passo-ora' : i < passo ? 'passo passo-fatto' : 'passo'}
          >
            <span className="passo-numero">{s.numero}</span>
            {s.titolo}
          </li>
        ))}
      </ol>

      {errore && (
        <div className="avviso" role="alert">
          {errore}
        </div>
      )}

      {mancanze.length > 0 && (
        <div className="avviso" role="alert">
          Before we can send your application, we need a few more things:
          <ul>
            {mancanze.map((m) => (
              <li key={m.campo}>{m.testo}</li>
            ))}
          </ul>
        </div>
      )}

      {passo === 0 && (
        <SezionePersona dati={dati} cambia={cambia} errorePer={errorePer} />
      )}
      {passo === 1 && <SezioneLavoro dati={dati} cambia={cambia} errorePer={errorePer} />}
      {passo === 2 && <SezioneEsperienza dati={dati} setDati={setDati} errorePer={errorePer} />}
      {passo === 3 && (
        <SezioneChiusura
          dati={dati}
          cambia={cambia}
          errorePer={errorePer}
          fileInput={fileInput}
          onFile={caricaFile}
          onConsenso={async (dato) => {
            try {
              const esito = await dichiaraConsenso(dato);
              setDati((d) => ({
                ...d,
                screeningConsentAt: esito.given ? new Date().toISOString() : null,
              }));
            } catch (e) {
              if (bozzaSparita(e)) return;
              setErrore(e instanceof ErroreApi ? e.message : 'Something went wrong.');
            }
          }}
        />
      )}

      {/* La trappola per i robot: fuori dallo schermo, fuori dal giro del
          tasto Tab, nascosta ai lettori di schermo. Una persona non la vede;
          un robot che riempie tutti i campi sì, e la sua candidatura resta
          una bozza. Vedi candidature-api/src/candidature/robot.ts */}
      <div className="trappola" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={trappola}
          onChange={(e) => setTrappola(e.target.value)}
        />
      </div>

      <div className="barra-fondo">
        <div className="barra-dentro">
          {passo > 0 && (
            <button type="button" className="btn" onClick={() => salvaEVai(passo - 1)}>
              Back
            </button>
          )}
          {passo < SEZIONI.length - 1 ? (
            <button
              type="button"
              className="btn btn-avanti"
              disabled={salvataggio === 'salvo'}
              onClick={() => salvaEVai(passo + 1)}
            >
              {salvataggio === 'salvo' ? 'Saving…' : 'Continue'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-avanti"
              disabled={salvataggio === 'salvo'}
              onClick={manda}
            >
              {salvataggio === 'salvo' ? 'Sending…' : 'Send my application'}
            </button>
          )}
          {salvataggio === 'salvato' && <span className="salvato">Saved</span>}
        </div>
      </div>
    </div>
  );
}

/** In quale sezione sta il campo che manca: si torna lì, non all'inizio. */
/** In quale sezione sta un campo: ci si torna per correggerlo. */
const CAMPI_DELLE_SEZIONI: string[][] = [
  [
    'firstName',
    'lastName',
    'middleInitial',
    'email',
    'phoneAreaCode',
    'phone',
    'addressStreet',
    'city',
    'state',
    'zip',
    'isOver18',
    'isAuthorizedToWork',
    'hasDriverLicense',
  ],
  [
    'positionDesired',
    'availableFrom',
    'salaryDesired',
    'employedNow',
    'mayContactEmployer',
    'appliedBefore',
    'appliedBeforeWhere',
    'appliedBeforeWhen',
    'referredBy',
  ],
  // «Skills and service» sta in fondo allo Step 3, non nello Step 4: segnati
  // lì, uno Special skills troppo lungo portava a un passo senza il campo
  [
    'schools',
    'employments',
    'references',
    'specialSkills',
    'currentCertifications',
    'militaryService',
    'militaryRank',
    'nationalGuardReserves',
  ],
  [
    'emergencyName',
    'emergencyAddress',
    'emergencyPhone',
    'convictedOfCrime',
    'crimeExplanation',
    'screeningConsent',
  ],
];

function sezioneDelCampo(campo: string): number | null {
  const radice = campo.split('.')[0] ?? '';
  const i = CAMPI_DELLE_SEZIONI.findIndex((nomi) => nomi.includes(radice));
  return i === -1 ? null : i;
}

type Cambia = <K extends keyof Candidatura>(chiave: K, valore: Candidatura[K]) => void;

function SezionePersona({
  dati,
  cambia,
  errorePer,
}: {
  dati: Partial<Candidatura>;
  cambia: Cambia;
  errorePer: (campo: string) => string | undefined;
}) {
  return (
    <>
      <section className="gruppo">
        <h2>Your name and address</h2>
        <p className="gruppo-nota">We need a way to reach you if we want to talk.</p>
        <div className="campi">
          <Campo etichetta="First name" obbligatorio errore={errorePer('firstName')}>
            <input
              type="text"
              autoComplete="given-name"
              value={dati.firstName ?? ''}
              onChange={(e) => cambia('firstName', e.target.value)}
            />
          </Campo>
          <Campo etichetta="Last name" obbligatorio errore={errorePer('lastName')}>
            <input
              type="text"
              autoComplete="family-name"
              value={dati.lastName ?? ''}
              onChange={(e) => cambia('lastName', e.target.value)}
            />
          </Campo>
          <Campo etichetta="Middle initial" errore={errorePer('middleInitial')}>
            <input
              type="text"
              maxLength={5}
              value={dati.middleInitial ?? ''}
              onChange={(e) => cambia('middleInitial', e.target.value)}
            />
          </Campo>
          <Campo etichetta="Street address" lungo errore={errorePer('addressStreet')}>
            <input
              type="text"
              autoComplete="street-address"
              value={dati.addressStreet ?? ''}
              onChange={(e) => cambia('addressStreet', e.target.value)}
            />
          </Campo>
          <Campo etichetta="City" errore={errorePer('city')}>
            <input
              type="text"
              autoComplete="address-level2"
              value={dati.city ?? ''}
              onChange={(e) => cambia('city', e.target.value)}
            />
          </Campo>
          <Campo etichetta="State" errore={errorePer('state')}>
            <input
              type="text"
              autoComplete="address-level1"
              value={dati.state ?? ''}
              onChange={(e) => cambia('state', e.target.value)}
            />
          </Campo>
          <Campo etichetta="ZIP" errore={errorePer('zip')}>
            <input
              type="text"
              autoComplete="postal-code"
              value={dati.zip ?? ''}
              onChange={(e) => cambia('zip', e.target.value)}
            />
          </Campo>
          <Campo etichetta="Area code" errore={errorePer('phoneAreaCode')}>
            <input
              type="tel"
              value={dati.phoneAreaCode ?? ''}
              onChange={(e) => cambia('phoneAreaCode', e.target.value)}
            />
          </Campo>
          <Campo etichetta="Telephone" errore={errorePer('phone')}>
            <input
              type="tel"
              autoComplete="tel-national"
              value={dati.phone ?? ''}
              onChange={(e) => cambia('phone', e.target.value)}
            />
          </Campo>
          <Campo
            etichetta="E-mail address"
            lungo
            obbligatorio
            errore={errorePer('email')}
            aiuto="An e-mail or a phone number — one of the two is enough."
          >
            <input
              type="email"
              autoComplete="email"
              value={dati.email ?? ''}
              onChange={(e) => cambia('email', e.target.value)}
            />
          </Campo>
        </div>
      </section>

      <section className="gruppo">
        <h2>Two questions we have to ask</h2>
        <div className="campi">
          <SiNo
            etichetta="Are you 18 years old or older?"
            valore={dati.isOver18}
            onCambia={(v) => cambia('isOver18', v)}
            obbligatorio
            errore={errorePer('isOver18')}
          />
          <SiNo
            etichetta="Are you legally authorized to work in the U.S.?"
            valore={dati.isAuthorizedToWork}
            onCambia={(v) => cambia('isAuthorizedToWork', v)}
            obbligatorio
            errore={errorePer('isAuthorizedToWork')}
          />
          <SiNo
            etichetta="Do you have a valid driver license?"
            valore={dati.hasDriverLicense}
            onCambia={(v) => cambia('hasDriverLicense', v)}
          />
        </div>
      </section>
    </>
  );
}

function SezioneLavoro({
  dati,
  cambia,
  errorePer,
}: {
  dati: Partial<Candidatura>;
  cambia: Cambia;
  errorePer: (campo: string) => string | undefined;
}) {
  return (
    <section className="gruppo">
      <h2>The job you are looking for</h2>
      <p className="gruppo-nota">
        If you are open to anything, write “Any” — it is a perfectly good answer.
      </p>
      <div className="campi">
        <Campo etichetta="Position desired" lungo errore={errorePer('positionDesired')}>
          <input
            type="text"
            value={dati.positionDesired ?? ''}
            onChange={(e) => cambia('positionDesired', e.target.value)}
          />
        </Campo>
        <Campo etichetta="Date you can start" errore={errorePer('availableFrom')}>
          <input
            type="date"
            value={dati.availableFrom ?? ''}
            onChange={(e) => cambia('availableFrom', e.target.value)}
          />
        </Campo>
        <Campo
          etichetta="Salary desired"
          aiuto="Per hour or per year — say which."
          errore={errorePer('salaryDesired')}
        >
          <input
            type="text"
            value={dati.salaryDesired ?? ''}
            onChange={(e) => cambia('salaryDesired', e.target.value)}
          />
        </Campo>

        <SiNo
          etichetta="Are you employed now?"
          valore={dati.employedNow}
          onCambia={(v) => cambia('employedNow', v)}
        />
        {dati.employedNow === true && (
          <SiNo
            etichetta="May we contact your present employer?"
            valore={dati.mayContactEmployer}
            onCambia={(v) => cambia('mayContactEmployer', v)}
          />
        )}

        <SiNo
          etichetta="Have you ever applied to this company before?"
          valore={dati.appliedBefore}
          onCambia={(v) => cambia('appliedBefore', v)}
        />
        {dati.appliedBefore === true && (
          <>
            <Campo etichetta="Where?" errore={errorePer('appliedBeforeWhere')}>
              <input
                type="text"
                value={dati.appliedBeforeWhere ?? ''}
                onChange={(e) => cambia('appliedBeforeWhere', e.target.value)}
              />
            </Campo>
            <Campo etichetta="When?" errore={errorePer('appliedBeforeWhen')}>
              <input
                type="text"
                value={dati.appliedBeforeWhen ?? ''}
                onChange={(e) => cambia('appliedBeforeWhen', e.target.value)}
              />
            </Campo>
          </>
        )}

        <Campo
          etichetta="Referred by"
          lungo
          aiuto="Someone who works here, an agency, a job board."
          errore={errorePer('referredBy')}
        >
          <input
            type="text"
            value={dati.referredBy ?? ''}
            onChange={(e) => cambia('referredBy', e.target.value)}
          />
        </Campo>
      </div>
    </section>
  );
}

function SezioneEsperienza({
  dati,
  setDati,
  errorePer,
}: {
  dati: Partial<Candidatura>;
  setDati: React.Dispatch<React.SetStateAction<Partial<Candidatura>>>;
  errorePer: (campo: string) => string | undefined;
}) {
  const scuolaDi = (kind: Scuola['kind']) => dati.schools?.find((s) => s.kind === kind);
  /** Le scuole stanno nell'ordine in cui si sono compilate: il server conta da lì. */
  const erroreScuola = (kind: Scuola['kind'], campo: keyof Scuola) => {
    const i = dati.schools?.findIndex((s) => s.kind === kind) ?? -1;
    return i === -1 ? undefined : errorePer(`schools.${i}.${campo}`);
  };

  const cambiaScuola = (kind: Scuola['kind'], campo: keyof Scuola, valore: unknown) => {
    setDati((d) => {
      const scuole = [...(d.schools ?? [])];
      const i = scuole.findIndex((s) => s.kind === kind);
      if (i === -1) scuole.push({ kind, [campo]: valore } as Scuola);
      else scuole[i] = { ...scuole[i]!, [campo]: valore };
      return { ...d, schools: scuole };
    });
  };

  const impieghi = dati.employments ?? [];
  const referenze = dati.references ?? [];

  const cambiaImpiego = (i: number, campo: keyof Impiego, valore: string) =>
    setDati((d) => {
      const lista = [...(d.employments ?? [])];
      lista[i] = { ...lista[i]!, [campo]: valore };
      return { ...d, employments: lista };
    });

  const cambiaReferenza = (i: number, campo: keyof Referenza, valore: string) =>
    setDati((d) => {
      const lista = [...(d.references ?? [])];
      lista[i] = { ...lista[i]!, [campo]: valore };
      return { ...d, references: lista };
    });

  return (
    <>
      <section className="gruppo">
        <h2>Education</h2>
        <p className="gruppo-nota">Fill in what applies to you. Leave the rest empty.</p>
        {SCUOLE.map((s) => {
          const scuola = scuolaDi(s.kind);
          return (
            <Blocco key={s.kind} titolo={s.etichetta}>
              <Campo etichetta="Name and location" lungo errore={erroreScuola(s.kind, 'name')}>
                <input
                  type="text"
                  value={scuola?.name ?? ''}
                  onChange={(e) => cambiaScuola(s.kind, 'name', e.target.value)}
                />
              </Campo>
              <Campo
                etichetta="Years completed"
                aiuto="How many years you attended, from 0 to 20"
                errore={erroreScuola(s.kind, 'yearsCompleted')}
              >
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={scuola?.yearsCompleted ?? ''}
                  onChange={(e) =>
                    cambiaScuola(s.kind, 'yearsCompleted', e.target.value ? Number(e.target.value) : null)
                  }
                />
              </Campo>
              <Campo etichetta="Did you graduate?">
                <select
                  value={scuola?.graduated === true ? 'yes' : scuola?.graduated === false ? 'no' : ''}
                  onChange={(e) =>
                    cambiaScuola(s.kind, 'graduated', e.target.value === '' ? null : e.target.value === 'yes')
                  }
                >
                  <option value="">—</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Campo>
              <Campo etichetta="Subjects studied" lungo errore={erroreScuola(s.kind, 'subjects')}>
                <input
                  type="text"
                  value={scuola?.subjects ?? ''}
                  onChange={(e) => cambiaScuola(s.kind, 'subjects', e.target.value)}
                />
              </Campo>
            </Blocco>
          );
        })}
      </section>

      <section className="gruppo">
        <h2>Former employment</h2>
        <p className="gruppo-nota">Most recent first. Add as many as you like — or none.</p>
        {impieghi.map((e, i) => (
          <Blocco
            key={i}
            titolo={`Employer ${i + 1}`}
            onTogli={() =>
              setDati((d) => ({ ...d, employments: (d.employments ?? []).filter((_, j) => j !== i) }))
            }
          >
            <Campo etichetta="Name of employer" lungo errore={errorePer(`employments.${i}.employerName`)}>
              <input
                type="text"
                value={e.employerName ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'employerName', ev.target.value)}
              />
            </Campo>
            {/* indirizzo, responsabile e paga: li chiedeva il v2, e la stampa ha i loro riquadri */}
            <Campo etichetta="Address" lungo errore={errorePer(`employments.${i}.address`)}>
              <input
                type="text"
                value={e.address ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'address', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="City" errore={errorePer(`employments.${i}.city`)}>
              <input
                type="text"
                value={e.city ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'city', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="State" errore={errorePer(`employments.${i}.state`)}>
              <input
                type="text"
                value={e.state ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'state', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="ZIP" errore={errorePer(`employments.${i}.zip`)}>
              <input
                type="text"
                inputMode="numeric"
                value={e.zip ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'zip', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Phone number" errore={errorePer(`employments.${i}.phone`)}>
              <input
                type="tel"
                value={e.phone ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'phone', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Your job title" errore={errorePer(`employments.${i}.jobTitle`)}>
              <input
                type="text"
                value={e.jobTitle ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'jobTitle', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Name of last supervisor" errore={errorePer(`employments.${i}.supervisorName`)}>
              <input
                type="text"
                value={e.supervisorName ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'supervisorName', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="From" errore={errorePer(`employments.${i}.fromDate`)}>
              <input
                type="date"
                value={e.fromDate ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'fromDate', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="To" errore={errorePer(`employments.${i}.toDate`)}>
              <input
                type="date"
                value={e.toDate ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'toDate', ev.target.value)}
              />
            </Campo>
            <Campo
              etichetta="Starting pay"
              aiuto="Per hour or per year — say which."
              errore={errorePer(`employments.${i}.startSalary`)}
            >
              <input
                type="text"
                value={e.startSalary ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'startSalary', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Final pay" errore={errorePer(`employments.${i}.finalSalary`)}>
              <input
                type="text"
                value={e.finalSalary ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'finalSalary', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Reason for leaving" lungo errore={errorePer(`employments.${i}.reasonForLeaving`)}>
              <input
                type="text"
                value={e.reasonForLeaving ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'reasonForLeaving', ev.target.value)}
              />
            </Campo>
            <Campo
              etichetta="What you did there"
              lungo
              aiuto="Duties, skills used or learned, promotions."
              errore={errorePer(`employments.${i}.dutiesAndSkills`)}
            >
              <textarea
                value={e.dutiesAndSkills ?? ''}
                onChange={(ev) => cambiaImpiego(i, 'dutiesAndSkills', ev.target.value)}
              />
            </Campo>
          </Blocco>
        ))}
        {/* il servizio ne tiene dieci: oltre, il passo si fermava con un
            messaggio e nessun campo rosso */}
        {(dati.employments?.length ?? 0) < 10 ? (
          <button
            type="button"
            className="btn"
            onClick={() => setDati((d) => ({ ...d, employments: [...(d.employments ?? []), {}] }))}
          >
            + Add an employer
          </button>
        ) : (
          <p className="aiuto">Ten employers is the most the form can hold.</p>
        )}
      </section>

      <section className="gruppo">
        <h2>References</h2>
        <p className="gruppo-nota">People who are not relatives or previous employers.</p>
        {referenze.map((r, i) => (
          <Blocco
            key={i}
            titolo={`Reference ${i + 1}`}
            onTogli={() =>
              setDati((d) => ({ ...d, references: (d.references ?? []).filter((_, j) => j !== i) }))
            }
          >
            <Campo etichetta="Name" errore={errorePer(`references.${i}.name`)}>
              <input
                type="text"
                value={r.name ?? ''}
                onChange={(ev) => cambiaReferenza(i, 'name', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Position" errore={errorePer(`references.${i}.role`)}>
              <input
                type="text"
                value={r.role ?? ''}
                onChange={(ev) => cambiaReferenza(i, 'role', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Company" errore={errorePer(`references.${i}.company`)}>
              <input
                type="text"
                value={r.company ?? ''}
                onChange={(ev) => cambiaReferenza(i, 'company', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Telephone" errore={errorePer(`references.${i}.phone`)}>
              <input
                type="tel"
                value={r.phone ?? ''}
                onChange={(ev) => cambiaReferenza(i, 'phone', ev.target.value)}
              />
            </Campo>
            <Campo etichetta="Address" lungo errore={errorePer(`references.${i}.address`)}>
              <input
                type="text"
                value={r.address ?? ''}
                onChange={(ev) => cambiaReferenza(i, 'address', ev.target.value)}
              />
            </Campo>
          </Blocco>
        ))}
        {/* il servizio ne tiene cinque */}
        {(dati.references?.length ?? 0) < 5 ? (
          <button
            type="button"
            className="btn"
            onClick={() => setDati((d) => ({ ...d, references: [...(d.references ?? []), {}] }))}
          >
            + Add a reference
          </button>
        ) : (
          <p className="aiuto">Five references is the most the form can hold.</p>
        )}
      </section>

      <section className="gruppo">
        <h2>Skills and service</h2>
        <div className="campi">
          <Campo etichetta="Special skills" lungo errore={errorePer('specialSkills')}>
            <textarea
              value={dati.specialSkills ?? ''}
              onChange={(e) => setDati((d) => ({ ...d, specialSkills: e.target.value }))}
            />
          </Campo>
          <Campo etichetta="Current certifications" lungo errore={errorePer('currentCertifications')}>
            <input
              type="text"
              value={dati.currentCertifications ?? ''}
              onChange={(e) => setDati((d) => ({ ...d, currentCertifications: e.target.value }))}
            />
          </Campo>
          <Campo etichetta="Active duty military service" errore={errorePer('militaryService')}>
            <input
              type="text"
              value={dati.militaryService ?? ''}
              onChange={(e) => setDati((d) => ({ ...d, militaryService: e.target.value }))}
            />
          </Campo>
          <Campo etichetta="Rank" errore={errorePer('militaryRank')}>
            <input
              type="text"
              value={dati.militaryRank ?? ''}
              onChange={(e) => setDati((d) => ({ ...d, militaryRank: e.target.value }))}
            />
          </Campo>
          <Campo etichetta="National Guard or Reserves" lungo errore={errorePer('nationalGuardReserves')}>
            <input
              type="text"
              value={dati.nationalGuardReserves ?? ''}
              onChange={(e) => setDati((d) => ({ ...d, nationalGuardReserves: e.target.value }))}
            />
          </Campo>
        </div>
      </section>
    </>
  );
}

function SezioneChiusura({
  dati,
  cambia,
  errorePer,
  fileInput,
  onFile,
  onConsenso,
}: {
  dati: Partial<Candidatura>;
  cambia: Cambia;
  errorePer: (campo: string) => string | undefined;
  fileInput: React.RefObject<HTMLInputElement | null>;
  onFile: (file: File) => void;
  onConsenso: (dato: boolean) => void;
}) {
  const file = dati.files?.[0];

  return (
    <>
      <section className="gruppo">
        <h2>Your resume</h2>
        <p className="gruppo-nota">
          PDF, Word or plain text, up to 8 MB. It is optional, but it helps.
        </p>
        {file ? (
          <div className="file-messo">
            <span>
              <strong>{file.originalName}</strong>
              <br />
              <span className="aiuto">{Math.max(1, Math.round(file.sizeBytes / 1024))} KB</span>
            </span>
            <button type="button" className="btn-testo" onClick={() => fileInput.current?.click()}>
              Replace
            </button>
          </div>
        ) : (
          <button type="button" className="zona-file" onClick={() => fileInput.current?.click()}>
            <strong>Attach your resume</strong>
            <span className="aiuto">PDF, Word or text</span>
          </button>
        )}
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.doc,.docx,.txt,.rtf"
          hidden
          onChange={(e) => {
            const scelto = e.target.files?.[0];
            if (scelto) onFile(scelto);
            e.target.value = '';
          }}
        />
      </section>

      <section className="gruppo">
        <h2>In case of emergency</h2>
        <p className="gruppo-nota">Someone we can call if something happens at work.</p>
        <div className="campi">
          <Campo etichetta="Name" lungo errore={errorePer('emergencyName')}>
            <input
              type="text"
              value={dati.emergencyName ?? ''}
              onChange={(e) => cambia('emergencyName', e.target.value)}
            />
          </Campo>
          <Campo etichetta="Address" lungo errore={errorePer('emergencyAddress')}>
            <input
              type="text"
              value={dati.emergencyAddress ?? ''}
              onChange={(e) => cambia('emergencyAddress', e.target.value)}
            />
          </Campo>
          <Campo etichetta="Phone" errore={errorePer('emergencyPhone')}>
            <input
              type="tel"
              value={dati.emergencyPhone ?? ''}
              onChange={(e) => cambia('emergencyPhone', e.target.value)}
            />
          </Campo>
        </div>
      </section>

      <section className="gruppo">
        <h2>Two last things</h2>
        <div className="riquadro-serio" style={{ marginBottom: 16 }}>
          <div className="campi">
            <SiNo
              etichetta="Have you ever been convicted of a crime?"
              valore={dati.convictedOfCrime}
              onCambia={(v) => cambia('convictedOfCrime', v)}
              aiuto="A conviction does not automatically rule you out. We look at what it was and when."
            />
            {dati.convictedOfCrime === true && (
              <Campo
                etichetta="Please explain"
                lungo
                obbligatorio
                errore={errorePer('crimeExplanation')}
              >
                <textarea
                  value={dati.crimeExplanation ?? ''}
                  onChange={(e) => cambia('crimeExplanation', e.target.value)}
                />
              </Campo>
            )}
          </div>
        </div>

        <div className={errorePer('screeningConsent') ? 'riquadro-serio campo-sbagliato' : 'riquadro-serio'}>
          <label className="casella">
            <input
              type="checkbox"
              checked={Boolean(dati.screeningConsentAt)}
              onChange={(e) => onConsenso(e.target.checked)}
            />
            <span>
              I understand that if I am selected for hire, I will be required to pass a
              pre-employment drug screen and to undergo a background check.
            </span>
          </label>
          {errorePer('screeningConsent') && (
            <p className="errore-campo" style={{ marginBottom: 0 }}>
              {errorePer('screeningConsent')}
            </p>
          )}
        </div>
      </section>
    </>
  );
}

function Fatta({ nome }: { nome: string }) {
  return (
    <div className="pagina">
      <div className="finita">
        <div className="finita-segno">✓</div>
        <h1>Thank you{nome ? `, ${nome}` : ''}</h1>
        <p className="sottotitolo">
          Your application is in. If your experience fits something we have open, someone from
          La Regina Atlantica will get in touch.
        </p>
      </div>
    </div>
  );
}
